import { useMemo } from "react";
import { useQuery } from "react-query";
import { fetchGoals } from "../../utilities/api";
import { getInvestmentYearLabel } from "../home/shared";
import { usePortfolioReturns } from "../home/useReturns";
import { useCurrency } from "../dropdown";
import { BASELINE_ANNUAL_PERCENT, buildGoalScenarios } from "./math";

// Shared by the Goals page and the dashboard summary card so the two surfaces
// can never disagree. Every figure returned is already in the selected display
// currency, so components render it straight.
//
// Goal amounts are the one exception to the API doing the conversion: /api/goals
// returns the amount exactly as it was saved, because the edit form has to
// prefill what the user typed. Portfolio worth arrives converted, so the target
// has to be brought into the same currency here -- comparing a converted worth
// against an unconverted target is what would make a goal read as met when it
// is not.
export const useGoalData = () => {
	const { data: goals, isLoading: isLoadingGoals } = useQuery("fetchGoals", fetchGoals, {
		refetchOnMount: true,
	});
	const { currency } = useCurrency();
	// Money-weighted, not the CAGR from the first trade: these projections
	// compound the rate forward for years, so starting from a figure that credits
	// late capital with a full history of growth compounds the error too. Skips
	// the daily price series, which nothing on this page reads.
	const {
		projectionPercent,
		currentWorth,
		usesMoneyWeighted,
		isLoading: isLoadingWorth,
	} = usePortfolioReturns({ timeWeighted: false });
	// Every figure here is worth-relative, so loading is not done until the worth
	// has landed. Reporting ready too early rendered a real goal as "0% of target
	// -- Behind -- invest 5,957,711/mo": a switch of display currency starts a new
	// fetch, and for the seconds it was in flight the zero read as fact.
	const isLoading = isLoadingGoals || isLoadingWorth;
	// Two rates, two answers. `historicalPercent` is what this portfolio has
	// actually returned; `baselinePercent` is the fair long-run assumption every
	// headline figure is built on, so a run of exceptional years never quietly
	// becomes the plan.
	const historicalPercent = projectionPercent;
	const baselinePercent = BASELINE_ANNUAL_PERCENT;
	const currentYear = getInvestmentYearLabel(new Date());
	const rate = currency?.value || 1;

	const rows = useMemo(
		() =>
			(goals || []).map((goal) =>
				buildGoalScenarios({
					goal: { ...goal, amount: Number(goal?.amount || 0) * rate },
					currentWorth,
					historicalPercent,
					baselinePercent,
				})
			),
		[goals, currentWorth, historicalPercent, baselinePercent, rate]
	);

	const yearlyRows = useMemo(
		() => rows.filter((row) => row.goal.type === "YEARLY"),
		[rows]
	);

	return {
		isLoading,
		rows,
		yearlyRows,
		currentYear,
		currentYearGoal:
			yearlyRows.find((row) => row.goal.year === currentYear) || null,
		nextYearGoal:
			yearlyRows.find((row) => row.goal.year === currentYear + 1) || null,
		finalGoal: rows.find((row) => row.goal.type === "FINAL") || null,
		historicalPercent,
		baselinePercent,
		usesMoneyWeighted,
		currentWorth,
	};
};
